//@ts-expect-error
import { QueryEMX2 } from "molgenis-components";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { extractValue } from "../functions/extractValue.js";
import { getPropertyByPath } from "../functions/getPropertyByPath.js";
import { buildSelectionPaths } from "../functions/serviceViewmodel.js";
import { useCollectionStore } from "./collectionStore.js";
import { useFiltersStore } from "./filtersStore.js";
import { useSettingsStore } from "./settingsStore.js";
import useErrorHandler from "../composables/errorHandler.js";
import { IBiobanks, ICollections, IServices } from "../interfaces/directory.js";

export const useBiobanksStore = defineStore("biobanksStore", () => {
  const settingsStore = useSettingsStore();
  const collectionStore = useCollectionStore();
  const filtersStore = useFiltersStore();
  const { setError, clearError } = useErrorHandler();

  const biobankReportColumns = settingsStore.config.biobankReportColumns;
  const biobankColumns = settingsStore.config.biobankColumns;
  const graphqlEndpoint = settingsStore.config.graphqlEndpoint;
  const dataModel = settingsStore.config.dataModel;
  const biobankCardGraphql = biobankReportColumns.flatMap(
    (biobankColumn) => biobankColumn.column
  );

  /** Fields the store needs regardless of display config: id/name for
   * row identity, navigation, and cart selection. parentBiobankName/Id are
   * synthetic (injected below in servicesForDisplay), never queried. */
  const SYNTHETIC_SERVICE_COLUMNS = new Set([
    "parentBiobankName",
    "parentBiobankId",
  ]);

  /** Reads settingsStore.config.serviceTableColumns live (not captured at
   * store-setup time): this store is instantiated as soon as the app boots
   * (App.vue -> filtersStore -> biobanksStore), well before the async
   * persisted-config fetch resolves, so a captured const would always see
   * the code default and never a persisted override. */
  function getServiceSelectionPaths() {
    const paths = new Set(["id", "name"]);

    for (const serviceColumn of settingsStore.config.serviceTableColumns) {
      if (SYNTHETIC_SERVICE_COLUMNS.has(serviceColumn.column)) continue;

      if (serviceColumn.type === "mref") {
        paths.add(`${serviceColumn.column}.label`);
        paths.add(`${serviceColumn.column}.name`);
      } else {
        paths.add(serviceColumn.column);
      }
    }

    return [...paths];
  }

  let facetBiobankColumnDetails = ref<string[]>([]);
  let biobankCards = ref<Record<string, any>[]>([]);
  let waitingForResponse = ref(true);

  // State for collections view
  let selectedBiobank = ref<IBiobanks | null>(null);
  let selectedCollection = ref<ICollections | null>(null);
  let selectedService = ref<IServices | null>(null);
  let allCollections = ref<ICollections[]>([]);
  let allServices = ref<IServices[]>([]);
  let biobankLookup = ref<Record<string, IBiobanks>>({});

  let lastRequestTime = 0;

  const collectionColumns = collectionStore.getCollectionColumns();
  /**
   * "services" is a display-only entry in biobankColumns (it renders the
   * capabilities summary row); its nested selection is supplied separately by
   * getBiobank(). Selecting it as a bare path would fail with
   * "Subselection required for type [Services]".
   */
  let biobankProperties = biobankColumns
    .flatMap((biobankColumn) => biobankColumn.column)
    .filter((property: unknown) => property !== "services");
  if (settingsStore.collectionsViewEnabled) {
    biobankProperties = biobankProperties.concat({
      collections: collectionColumns,
    });
  }

  const baseQuery = new QueryEMX2(graphqlEndpoint)
    .table(dataModel.providerTable)
    .orderBy(dataModel.providerTable, "name", "asc");
  if (settingsStore.collectionsViewEnabled) {
    baseQuery.orderBy("collections", "id", "asc");
  }

  const biobankCardsHaveResults = computed(
    () => !waitingForResponse.value && biobankCards.value?.length
  );

  const biobankCardsBiobankCount = computed(() => {
    return (
      biobankCards.value?.filter((biobankCard) => !biobankCard.withdrawn)
        .length || 0
    );
  });

  const biobankCardsSubcollectionCount = computed(() => {
    if (!biobankCards.value.length) {
      return 0;
    } else {
      return biobankCards.value
        .filter((bc) => bc.collections)
        .flatMap((biobank) =>
          biobank.collections.filter(
            (collection: ICollections) =>
              !collection.withdrawn && collection.parent_collection
          )
        ).length;
    }
  });

  const biobankCardsCollectionCount = computed(() => {
    const totalCount = biobankCards.value
      .filter((bc) => bc.collections)
      .flatMap((biobank) =>
        biobank.collections.filter(
          (collection: ICollections) => !collection.withdrawn
        )
      ).length;
    return totalCount - biobankCardsSubcollectionCount.value;
  });

  const biobankCardsServicesCount = computed(
    () =>
      biobankCards.value
        .filter((bc) => bc.services)
        .flatMap((biobank) => biobank.services).length
  );

  // Computed property for collections display
  const collectionsForDisplay = computed(() => {
    if (!biobankCards.value || !biobankCards.value.length) return [];
    
    return biobankCards.value.flatMap(biobank =>
      biobank.collections?.map((collection: ICollections) => ({
        ...collection,
        parentBiobankId: biobank.id,
        parentBiobankName: biobank.name
      })) || []
    );
  });

  const servicesForDisplay = computed(() => {
    if (!biobankCards.value || !biobankCards.value.length) return [];

    return biobankCards.value.flatMap(
      (biobank) =>
        biobank.services?.map((service: IServices) => ({
          ...service,
          parentBiobankId: biobank.id,
          parentBiobankName: biobank.name,
        })) || []
    );
  });

  function getFacetColumnDetails() {
    if (!facetBiobankColumnDetails.value.length) {
      const filterFacetProperties = [];

      const filterFacets = settingsStore.config.filterFacets.filter(
        (facet) =>
          !facet.builtIn &&
          facet.filterValueAttribute &&
          facet.filterLabelAttribute
      );

      for (const facet of filterFacets) {
        const columns = Array.isArray(facet.applyToColumn)
          ? facet.applyToColumn
          : [facet.applyToColumn];

        for (const column of columns) {
          let nestedpath = column.split(".");
          /** pop off the trail because that is not what the user always needs */
          nestedpath.pop();

          const nestedPathBase = nestedpath.join(".");

          filterFacetProperties.push(
            `${nestedPathBase}.${facet.filterLabelAttribute}`,
            `${nestedPathBase}.${facet.filterValueAttribute}`
          );
        }
        facetBiobankColumnDetails.value = filterFacetProperties;
      }
    }

    return facetBiobankColumnDetails.value;
  }

  /** This method is called upon page load and when a filter is applied. */
  /** Therefore it can be executed multiple times in parallel. */
  async function getBiobankCards() {
    if (!filtersStore.bookmarkWaitingForApplication) {
      waitingForResponse.value = true;

      /** Rebuilt on every call (not once at store setup) so it always
       * reflects the current serviceTableColumns config, see comment on
       * getServiceSelectionPaths. */
      baseQuery.select([
        ...biobankCardGraphql,
        ...getFacetColumnDetails(),
        { services: getServiceSelectionPaths() },
      ]);

      const requestTime = Date.now();
      lastRequestTime = requestTime;

      let biobankResult = [];
      try {
        clearError();
        biobankResult = await baseQuery.execute();
      } catch (error) {
        setError(error);
      }

      /* Update biobankCards only if the result is the most recent one*/
      if (requestTime === lastRequestTime) {
        let foundBiobanks = biobankResult[dataModel.providerTable];
        /* Only meaningful when collections are part of the datamodel:
         * without this gate every filter would drop all cards, because
         * collections are never selected. */
        if (
          settingsStore.collectionsViewEnabled &&
          filtersStore.hasActiveFilters &&
          !filtersStore.hasActiveBiobankOnlyFilters
        ) {
          foundBiobanks = foundBiobanks?.filter(
            (biobank: IBiobanks) => biobank.collections
          );
        }
        biobankCards.value = filterWithdrawn(foundBiobanks);
        waitingForResponse.value = false;
        filtersStore.bookmarkWaitingForApplication = false;
      }
    }
  }

  /**
   * Selection for the services shown in the provider report's Services tab
   * (ServiceDetails.vue). Derived from serviceReportColumns so the same config
   * drives what is queried and what is displayed, and read live rather than at
   * store-setup time - see the comment on getServiceSelectionPaths.
   */
  function getServiceDetailSelectionPaths() {
    return buildSelectionPaths(settingsStore.config.serviceReportColumns, {
      always: ["id", "name", `${dataModel.providerRefOnService}.id`, `${dataModel.providerRefOnService}.name`],
    });
  }

  async function getBiobank(id: string) {
    const biobankReportQuery = new QueryEMX2(graphqlEndpoint)
      .table(dataModel.providerTable)
      .select([
        ...biobankProperties,
        { services: getServiceDetailSelectionPaths() },
      ])
      .orderBy(dataModel.providerTable, "name", "asc");
    if (settingsStore.collectionsViewEnabled) {
      biobankReportQuery.orderBy("collections", "id", "asc");
    }
    biobankReportQuery.where("id").equals(id);
    try {
      clearError();
      return await biobankReportQuery.execute();
    } catch (error) {
      setError(error);
      return null;
    }
  }

  function getPresentFilterOptions(facetIdentifier: string) {
    const { applyToColumn, adaptive } =
      filtersStore.facetDetails[facetIdentifier];
    if (
      biobankCards.value === undefined ||
      !biobankCards.value.length ||
      !adaptive
    ) {
      return [];
    }

    let columnPath = applyToColumn;
    if (!Array.isArray(applyToColumn)) {
      columnPath = [applyToColumn];
    }

    let valuesKnown;

    for (const path of columnPath) {
      const pathParts = path.split(".");

      if (pathParts[0] === "collections") {
        pathParts.shift(); /** removing 'collections' */
        const finalProperty = pathParts.pop();

        const collectionsFromBiobanks = biobankCards.value.flatMap(
          (biobank) => biobank.collections
        );

        if (collectionsFromBiobanks.length === 0) break;

        let valuesPresent = collectionsFromBiobanks.flatMap((collection) => {
          return extractValue(
            getPropertyByPath(collection, pathParts),
            finalProperty
          );
        });

        if (!valuesKnown) {
          valuesKnown = valuesPresent.filter((uv) => uv);
        } else {
          valuesKnown = valuesPresent.concat(valuesKnown).filter((uv) => uv);
        }
      } else {
        const finalProperty = pathParts.pop();
        const valuesPresent = biobankCards.value.map((biobank) => {
          return extractValue(
            getPropertyByPath(biobank, pathParts),
            finalProperty
          );
        });

        if (!valuesKnown) {
          valuesKnown = valuesPresent.filter((uv) => uv);
        } else {
          valuesKnown = valuesPresent.concat(valuesKnown).filter((uv) => uv);
        }
      }
    }
    //@ts-ignore
    return [...new Set(valuesKnown)];
  }

  // Methods for collections view
  function getAllCollections() {
    const collections = collectionsForDisplay.value;
    allCollections.value = collections;
    buildBiobankLookup();
    return collections;
  }

  function getAllServices() {
    const services = servicesForDisplay.value;
    allServices.value = services;
    buildBiobankLookup();
    return services;
  }

  function buildBiobankLookup() {
    const lookup: Record<string, IBiobanks> = {};
    biobankCards.value?.forEach((biobank: IBiobanks) => {
      lookup[biobank.id] = biobank;
    });
    biobankLookup.value = lookup;
  }

  function selectCollection(collectionId: string) {
    buildBiobankLookup();
    const collection =
      collectionsForDisplay.value.find((c) => c.id === collectionId) ||
      allCollections.value.find((c) => c.id === collectionId);
    if (collection) {
      selectedCollection.value = collection;
      selectedService.value = null;
      const biobank = biobankLookup.value[(collection as any).parentBiobankId];
      selectedBiobank.value = biobank || null;
    }
  }

  function selectService(serviceId: string) {
    buildBiobankLookup();
    const service =
      servicesForDisplay.value.find((s) => s.id === serviceId) ||
      allServices.value.find((s) => s.id === serviceId);
    if (service) {
      selectedService.value = service;
      selectedCollection.value = null;
      const biobank = biobankLookup.value[(service as any).parentBiobankId];
      selectedBiobank.value = biobank || null;
    }
  }

  function clearSelection() {
    selectedBiobank.value = null;
    selectedCollection.value = null;
    selectedService.value = null;
  }

  return {
    getBiobankCards,
    getBiobank,
    getPresentFilterOptions,
    getAllCollections,
    getAllServices,
    selectCollection,
    selectService,
    clearSelection,
    waiting: waitingForResponse,
    biobankCardsHaveResults,
    biobankCardsBiobankCount,
    biobankCardsCollectionCount,
    biobankCardsServicesCount,
    biobankCardsSubcollectionCount,
    collectionsForDisplay,
    servicesForDisplay,
    biobankCards,
    selectedBiobank,
    selectedCollection,
    selectedService,
    allCollections,
    allServices,
    biobankLookup,
    baseQuery,
  };
});

function filterWithdrawn(biobanks: IBiobanks[] | undefined): IBiobanks[] {
  let filteredBanks = biobanks?.filter((biobank) => !biobank.withdrawn) || [];
  filteredBanks.forEach((biobank) => {
    biobank.collections = biobank.collections?.filter(
      (collection) => !collection.withdrawn
    );
  });
  return filteredBanks;
}
